import React from 'react';

import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { FilterIcon } from '@revfluence/fresh-icons/solid';

import { DismissableBadge } from '@/applications/Social/components/filters/badges/dismissable-badge';
import type { CategorizedLoadingStates } from '../../hooks/use-loading-states';
import CreatorDemographicsAgeBadge from './creator-demographics-age';
import CreatorDemographicsCountriesBadge from './creator-demographics-countries';
import CreatorDemographicsGenderBadge from './creator-demographics-gender';
import CreatorDemographicsInterestsBadge from './creator-demographics-interests';
import CreatorDemographicsMaxEngagementsBadge from './creator-demographics-max-engagements';
import CreatorDemographicsMaxFollowersBadge from './creator-demographics-max-followers';
import CreatorDemographicsMinEngagementsBadge from './creator-demographics-min-engagements';
import CreatorDemographicsMinFollowersBadge from './creator-demographics-min-followers';
import DemographicsAgeBadge from './demographics-age';
import DemographicsCountriesBadge from './demographics-countries';
import DemographicsGenderBadge from './demographics-gender';
import HasEmailBadge from './has-email';
import { Badge } from './hooks/use-search-badges';
import InstagramCreatorMarketplaceBadge from './instagram-creator-marketplace';
import SimilarCreatorsBadge from './similar-creators';
import UsernameBadge from './username';

interface CreatorSearchBadgesProps {
  badges: Record<string, Badge>;
  onClick: () => void;
  onDismiss: (id: string) => (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onDismissItem: (id: string, itemName: string) => void;
  onClearAll: () => void;
  loadingStates: CategorizedLoadingStates;
}

const BadgeSkeletons = () => (
  <div className="flex justify-start flex-wrap">
    <Skeleton className="h-8 w-24 rounded-full mr-2 mb-2" aria-label="Loading filter badge" />
    <Skeleton className="h-8 w-32 rounded-full mr-2 mb-2" aria-label="Loading filter badge" />
    <Skeleton className="h-8 w-28 rounded-full mr-2 mb-2" aria-label="Loading filter badge" />
    <Skeleton className="h-8 w-20 rounded-full mr-2 mb-2" aria-label="Loading filter badge" />
    <Skeleton className="h-8 w-36 rounded-full mr-2 mb-2" aria-label="Loading filter badge" />
  </div>
);

const CreatorSearchBadges = ({
  badges,
  onClick,
  onDismiss,
  onDismissItem,
  onClearAll,
  loadingStates,
}: CreatorSearchBadgesProps) => {
  const {
    ageBuckets,
    countries,
    genders,
    interests,
    minFollowers,
    maxFollowers,
    minEngagements,
    maxEngagements,
    majorAudienceAgeBuckets,
    majorAudienceCountries,
    majorAudienceGenders,
    similarToCreators,
    usernames,
    hasEmail,
    requireInCreatorMarketplace,
  } = badges;

  // Extract UI states from loading states hook
  const { shouldShowFiltersLoading } = loadingStates.uiStates;

  // Show skeleton only during AI filter generation
  if (shouldShowFiltersLoading) {
    return <BadgeSkeletons />;
  }

  return (
    <div className="flex justify-start flex-wrap">
      {/* Creator Badges */}
      {ageBuckets && ageBuckets.names.length > 0 && (
        <CreatorDemographicsAgeBadge data={ageBuckets} onClick={onClick} onDismiss={onDismiss(ageBuckets.id)} />
      )}
      {genders && genders.names.length > 0 && !genders.names.includes('All') && (
        <CreatorDemographicsGenderBadge data={genders} onClick={onClick} onDismiss={onDismiss(genders.id)} />
      )}
      {countries && countries.names.length > 0 && (
        <CreatorDemographicsCountriesBadge data={countries} onClick={onClick} onDismiss={onDismiss(countries.id)} />
      )}
      {minFollowers && minFollowers.names.length > 0 && (
        <CreatorDemographicsMinFollowersBadge
          data={minFollowers}
          onClick={onClick}
          onDismiss={onDismiss(minFollowers.id)}
        />
      )}
      {maxFollowers && maxFollowers.names.length > 0 && (
        <CreatorDemographicsMaxFollowersBadge
          data={maxFollowers}
          onClick={onClick}
          onDismiss={onDismiss(maxFollowers.id)}
        />
      )}
      {minEngagements && minEngagements.names.length > 0 && (
        <CreatorDemographicsMinEngagementsBadge
          data={minEngagements}
          onClick={onClick}
          onDismiss={onDismiss(minEngagements.id)}
        />
      )}
      {maxEngagements && maxEngagements.names.length > 0 && (
        <CreatorDemographicsMaxEngagementsBadge
          data={maxEngagements}
          onClick={onClick}
          onDismiss={onDismiss(maxEngagements.id)}
        />
      )}
      {interests && interests.names.length > 0 && (
        <CreatorDemographicsInterestsBadge data={interests} onClick={onClick} onDismiss={onDismiss(interests.id)} />
      )}
      {similarToCreators && similarToCreators.names.length > 0 && (
        <SimilarCreatorsBadge
          data={similarToCreators}
          onClick={onClick}
          onDismiss={onDismiss(similarToCreators.id)}
          onDismissItem={(itemName) => onDismissItem(similarToCreators.id, itemName)}
        />
      )}
      {usernames && usernames.names.length > 0 && (
        <UsernameBadge
          usernames={usernames.names}
          onDismissItem={(itemName) => onDismissItem(usernames.id, itemName)}
        />
      )}
      {hasEmail && hasEmail.names.length > 0 && (
        <HasEmailBadge data={hasEmail} onClick={onClick} onDismiss={onDismiss(hasEmail.id)} />
      )}
      {requireInCreatorMarketplace && requireInCreatorMarketplace.names.length > 0 && (
        <InstagramCreatorMarketplaceBadge
          data={requireInCreatorMarketplace}
          onClick={onClick}
          onDismiss={onDismiss(requireInCreatorMarketplace.id)}
        />
      )}

      {/* Audience Demographics - Use refined audience-specific badges */}
      {majorAudienceAgeBuckets && (
        <DemographicsAgeBadge
          data={majorAudienceAgeBuckets}
          onClick={onClick}
          onDismiss={onDismiss(majorAudienceAgeBuckets.id)}
        />
      )}
      {majorAudienceGenders && (
        <DemographicsGenderBadge
          data={majorAudienceGenders}
          onClick={onClick}
          onDismiss={onDismiss(majorAudienceGenders.id)}
        />
      )}
      {majorAudienceCountries && (
        <DemographicsCountriesBadge
          data={majorAudienceCountries}
          onClick={onClick}
          onDismiss={onDismiss(majorAudienceCountries.id)}
        />
      )}

      <DismissableBadge
        variant="secondary"
        className={`mr-2 mb-2 ${shouldShowFiltersLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        aria-label="Filters"
        role="button"
        data-dd-action-name="open-analytics-filters"
        onClick={shouldShowFiltersLoading ? undefined : onClick}
      >
        <FilterIcon className="mr-2" />
        Filters
        {shouldShowFiltersLoading && (
          <span
            className="ml-1 inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"
            aria-hidden="true"
          />
        )}
      </DismissableBadge>

      {/* clear all filters badge  */}
      <DismissableBadge
        variant="secondary"
        className={`mr-2 mb-2 ${shouldShowFiltersLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        aria-label="Clear all filters"
        role="button"
        onClick={shouldShowFiltersLoading ? undefined : onClearAll}
      >
        <XmarkIcon className="mr-2" />
        Clear all
      </DismissableBadge>
    </div>
  );
};

export default CreatorSearchBadges;
